import { usePortalPosition } from '@/hooks/use-portal-position';
import React, { ReactNode, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Portal } from './portal';

interface PortalDropdownProps {
    open: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<any>;
    outsideClickRef?: React.RefObject<any>;
    children: ReactNode;
    offsetY?: number;
    maxHeight?: number;
}

export function PortalDropdown({
    open,
    onClose,
    anchorRef,
    outsideClickRef,
    children,
    offsetY = 4,
    maxHeight = 300,
}: PortalDropdownProps) {
    const dropdownRef = useRef<View>(null);

    const { layout, measure } = usePortalPosition({
        anchorRef,
        dropdownRef,
        outsideClickRef,
        open,
        onClose,
        offsetY,
    });

    // Measure immediately when opening
    React.useEffect(() => {
        if (open) {
            measure();
        }
    }, [open, measure]);

    if (!open || !layout) return null;

    return (
        <Portal>
            <View
                ref={dropdownRef}
                style={[
                    styles.dropdown,
                    {
                        position: Platform.OS === 'web' ? 'fixed' : 'absolute',
                        top: layout.y + layout.height + offsetY,
                        left: layout.x,
                        width: layout.width,
                        maxHeight,
                    },
                ]}>
                {children}
            </View>
        </Portal>
    );
}

const styles = StyleSheet.create({
    dropdown: {
        backgroundColor: '#fff',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,0.12)',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
        elevation: 5,
        overflow: 'hidden',
    },
});
